// kernel/useWorkspaceTimeline.ts
import { useCallback, type RefObject } from 'react';
import type { KernelHandle, Layer, UseWorkspaceTimelineReturnType } from '#types/types';
import {
  AUDIO_GAIN_MAX,
  DEFAULT_TIMELINE_EFFECT_RATIO,
  TIMELINE_EFFECT_ENABLED_EPSILON,
  TIMELINE_AUDIO_GAIN_PARAM_KEY,
  TIMELINE_AUDIO_ONLY_PARAM_KEY,
  TIMELINE_FADE_IN_RATIO_PARAM_KEY,
  TIMELINE_FADE_OUT_RATIO_PARAM_KEY,
  TIMELINE_LANE_ID_PARAM_KEY,
  MIN_TIMELINE_TRACK_SPAN_MS,
  TIMELINE_LANE_ORDER_PARAM_KEY,
  TIMELINE_MUTE_AUDIO_PARAM_KEY,
  TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY,
  buildWorkspaceTimelineProjection,
  clampTimelineNumber,
  getWorkspaceTimelineLayerKind,
  mergeTimelineParams,
  resolveWorkspaceTimelineSplitMsAtClientX as resolveTimelineSplitMsAtClientX,
  resolveWorkspaceTimelineTrackAtClientX as resolveTimelineTrackAtClientX,
  toFiniteTimelineMs,
  type WorkspaceTimelineProjection,
  type WorkspaceTimelineSnapshot,
  type WorkspaceTimelineTrackView,
} from '#lib/workspace-timeline';
import { normalizeVideoDimension, type TimelineCompositionStatus } from '#lib/timeline-video-types';
import { waitForTimelineExportReadyViaSocket } from '@/components/workspace-timeline/useTimelineExportRealtimeStatus';

interface UseWorkspaceTimelineProps {
  kernel: RefObject<KernelHandle | null>;
}

const MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;
// Default lifetime of an image track = one frame (T1 = T0 + 1 frame). Seeded
// image layers are chained by this step, so each image occupies a single frame
// rather than a fixed one-second block.
const DEFAULT_IMAGE_TRACK_DURATION_MS = MIN_TIMELINE_TRACK_SPAN_MS;

const normalizeStringId = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const uniqueStringIds = (values: unknown[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const normalized = normalizeStringId(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
};

const cloneParams = (params: unknown): Record<string, unknown> | undefined => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return undefined;
  return { ...(params as Record<string, unknown>) };
};

const cloneLayerForTimelineUndo = (layer: Layer): Layer => ({
  ...layer,
  rect: { ...layer.rect },
  params: cloneParams(layer.params) as Layer['params'],
});

const clampCursorForDuration = (cursorMs: number, durationMs: number): number => {
  const safeDurationMs = Math.max(0, Math.round(durationMs));
  const safeCursorMs = Math.max(0, Math.round(cursorMs));
  return safeDurationMs > 0 ? Math.min(safeCursorMs, Math.max(0, safeDurationMs - 1)) : safeCursorMs;
};

const applyLayerPatch = (
  kernel: RefObject<KernelHandle | null>,
  layerId: string,
  buildNext: (layer: Layer) => Layer,
  options?: { undoable?: boolean; description?: string }
): void => {
  const previousLayer = kernel.current?.getLayerRegistry()[layerId];
  if (!previousLayer) return;
  const previousSnapshot = cloneLayerForTimelineUndo(previousLayer);
  const nextSnapshot = buildNext(previousLayer);

  if (options?.undoable) {
    kernel.current?.executeAction({
      description: options.description ?? `Update timeline track "${previousLayer.name || layerId}"`,
      redo: () => {
        kernel.current?.setLayer(layerId, () => nextSnapshot);
      },
      undo: () => {
        kernel.current?.setLayer(layerId, () => previousSnapshot);
      },
    });
    return;
  }

  kernel.current?.setLayer(layerId, () => nextSnapshot);
};

const resolveProjection = (
  kernel: RefObject<KernelHandle | null>,
  projectId: string,
  workspaceId: string
): WorkspaceTimelineProjection | null => {
  const normalizedProjectId = normalizeStringId(projectId);
  const normalizedWorkspaceId = normalizeStringId(workspaceId);
  if (!normalizedProjectId || !normalizedWorkspaceId) return null;

  const workspace = kernel.current?.getWorkspace(normalizedWorkspaceId);
  if (!workspace) return null;
  const layerRegistry = kernel.current?.getLayerRegistry() ?? {};
  const projectRuntimeSelection = kernel.current?.readProjectRuntimeState(normalizedProjectId).selectedLayersId ?? [];
  const pendingTasks = kernel.current?.getGenerationTasks(normalizedProjectId) ?? [];

  return buildWorkspaceTimelineProjection({
    projectId: normalizedProjectId,
    workspaceId: normalizedWorkspaceId,
    workspace,
    layerRegistry,
    projectRuntimeSelection,
    pendingTasks,
  });
};

const resolveProjectionForWorkspace = (
  kernel: RefObject<KernelHandle | null>,
  workspaceId: string
): WorkspaceTimelineProjection | null => {
  const normalizedWorkspaceId = normalizeStringId(workspaceId);
  if (!normalizedWorkspaceId) return null;
  const workspace = kernel.current?.getWorkspace(normalizedWorkspaceId);
  const projectId = normalizeStringId(workspace?.projectId);
  if (!projectId) return null;
  return resolveProjection(kernel, projectId, normalizedWorkspaceId);
};

const projectionToSnapshot = (projection: WorkspaceTimelineProjection): WorkspaceTimelineSnapshot => ({
  workspaceId: projection.workspaceId,
  projectId: projection.projectId,
  summary: projection.summary,
  trackLayerIds: [...projection.trackLayerIds],
  tracks: projection.tracks.map((track) => ({ ...track, rect: { ...track.rect } })),
  lanes: projection.lanes.map((lane) => ({
    ...lane,
    trackLayerIds: [...lane.trackLayerIds],
  })),
});

export const useWorkspaceTimeline = ({ kernel }: UseWorkspaceTimelineProps): UseWorkspaceTimelineReturnType => {
  const getWorkspaceTimelineSummary = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineSummary']>((workspaceId) => {
    if (!kernel.current) return null;
    return resolveProjectionForWorkspace(kernel, workspaceId)?.summary ?? null;
  }, [kernel]);

  const getWorkspaceTimelineSnapshot = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineSnapshot']>((workspaceId) => {
    if (!kernel.current) return null;
    const projection = resolveProjectionForWorkspace(kernel, workspaceId);
    return projection ? projectionToSnapshot(projection) : null;
  }, [kernel]);

  const getWorkspaceTimelineProjection = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineProjection']>((projectId, workspaceId) => {
    if (!kernel.current) return null;
    return resolveProjection(kernel, projectId, workspaceId);
  }, [kernel]);

  const getWorkspaceTimelineResolvedTracks = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineResolvedTracks']>((projectId, workspaceId) => {
    if (!kernel.current) return [];
    return resolveProjection(kernel, projectId, workspaceId)?.resolvedTracks ?? [];
  }, [kernel]);

  const getWorkspaceTimelineTrack = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineTrack']>((layerId) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return null;
    const layer = kernel.current.getLayerRegistry()[normalizedLayerId];
    const workspaceId = normalizeStringId(layer?.layerWorkspaceId);
    if (!workspaceId) return null;
    return resolveProjectionForWorkspace(kernel, workspaceId)?.tracks.find((track) => track.layerId === normalizedLayerId) ?? null;
  }, [kernel]);

  const getWorkspaceTimelineTrackById = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineTrackById']>((layerId, workspaceId, projectId) => {
    const normalizedLayerId = normalizeStringId(layerId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedLayerId) return null;

    if (normalizedWorkspaceId) {
      const normalizedProjectId = normalizeStringId(projectId) || normalizeStringId(kernel.current.getWorkspace(normalizedWorkspaceId)?.projectId);
      if (!normalizedProjectId) return null;
      return resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId)?.tracks.find((track) => track.layerId === normalizedLayerId) ?? null;
    }

    return kernel.current.getWorkspaceTimelineTrack(normalizedLayerId);
  }, [kernel]);

  const getWorkspaceTimelineSelectedTracks = useCallback<UseWorkspaceTimelineReturnType['getWorkspaceTimelineSelectedTracks']>((projectId, workspaceId) => {
    if (!kernel.current) return [];
    return resolveProjection(kernel, projectId, workspaceId)?.selectedTracks ?? [];
  }, [kernel]);

  const resolveWorkspaceTimelineTrackAtClientX = useCallback<UseWorkspaceTimelineReturnType['resolveWorkspaceTimelineTrackAtClientX']>((args) => (
    resolveTimelineTrackAtClientX(args)
  ), []);

  const resolveWorkspaceTimelineSplitMsAtClientX = useCallback<UseWorkspaceTimelineReturnType['resolveWorkspaceTimelineSplitMsAtClientX']>((args) => (
    resolveTimelineSplitMsAtClientX(args)
  ), []);

  const listWorkspaceTimelineTrackIds = useCallback<UseWorkspaceTimelineReturnType['listWorkspaceTimelineTrackIds']>((workspaceId) => {
    if (!kernel.current) return [];
    return resolveProjectionForWorkspace(kernel, workspaceId)?.trackLayerIds ?? [];
  }, [kernel]);

  const setWorkspaceTimelineCursor = useCallback<UseWorkspaceTimelineReturnType['setWorkspaceTimelineCursor']>((
    projectId,
    workspaceId,
    cursorMs,
    options
  ) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId || !Number.isFinite(cursorMs)) return;

    const normalizedCursorMs = Math.max(0, Math.round(cursorMs));
    const minDeltaMs = Math.max(0, Math.round(toFiniteTimelineMs(options?.minDeltaMs) ?? 0));
    kernel.current.setWorkspace(normalizedWorkspaceId, (workspace) => {
      const currentCursorMs = Math.max(0, Math.round(toFiniteTimelineMs(workspace.timelineCursorMs) ?? 0));
      if (minDeltaMs > 0 && Math.abs(currentCursorMs - normalizedCursorMs) < minDeltaMs) {
        return workspace;
      }
      return {
        ...workspace,
        timelineCursorMs: normalizedCursorMs,
      };
    }, normalizedProjectId);
  }, [kernel]);

  const ensureWorkspaceImageTimelineInitialized = useCallback<UseWorkspaceTimelineReturnType['ensureWorkspaceImageTimelineInitialized']>((
    projectId,
    workspaceId
  ) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId) return;

    const workspace = kernel.current.getWorkspace(normalizedWorkspaceId);
    if (!workspace) return;

    const layerRegistry = kernel.current.getLayerRegistry();
    const imageLayerIds = Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((layerId): layerId is string => {
          if (typeof layerId !== 'string' || layerId.trim().length === 0) return false;
          return getWorkspaceTimelineLayerKind(layerRegistry[layerId]) === 'image';
        })
      : [];
    if (imageLayerIds.length === 0) return;

    const hasExplicitImageTiming = imageLayerIds.some((layerId) => {
      const layer = layerRegistry[layerId];
      return toFiniteTimelineMs(layer?.timelineStartMs) !== null || toFiniteTimelineMs(layer?.timelineEndMs) !== null;
    });

    if (!hasExplicitImageTiming) {
      imageLayerIds.forEach((layerId, index) => {
        const startMs = index * DEFAULT_IMAGE_TRACK_DURATION_MS;
        const endMs = startMs + DEFAULT_IMAGE_TRACK_DURATION_MS;
        kernel.current?.setLayer(layerId, (layer) => ({
          ...layer,
          timelineStartMs: startMs,
          timelineEndMs: endMs,
          timelineSourceInMs: 0,
        }));
      });
    }

    const seededDurationMs = Math.max(DEFAULT_IMAGE_TRACK_DURATION_MS, imageLayerIds.length * DEFAULT_IMAGE_TRACK_DURATION_MS);
    kernel.current?.setWorkspace(normalizedWorkspaceId, (currentWorkspace) => {
      const existingDurationMs = Math.max(0, toFiniteTimelineMs(currentWorkspace.timelineDurationMs) ?? 0);
      const nextDurationMs = Math.max(existingDurationMs, seededDurationMs);
      return {
        ...currentWorkspace,
        timelineDurationMs: nextDurationMs,
        timelineCursorMs: clampCursorForDuration(toFiniteTimelineMs(currentWorkspace.timelineCursorMs) ?? 0, nextDurationMs),
        preferVersionPanelOverTimeline: false,
        imageTimelineManuallyEnabled: true,
      };
    }, normalizedProjectId);
  }, [kernel]);

  const toggleWorkspaceTimelineView = useCallback<UseWorkspaceTimelineReturnType['toggleWorkspaceTimelineView']>((projectId, workspaceId) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId) return;

    const projection = resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId);
    if (!projection) return;

    const nextPreferVersionView = !projection.summary.preferVersionView;
    kernel.current.setWorkspace(normalizedWorkspaceId, (workspace) => ({
      ...workspace,
      preferVersionPanelOverTimeline: nextPreferVersionView,
      imageTimelineManuallyEnabled: projection.summary.hasPlayableMediaLayers ? false : !nextPreferVersionView,
    }), normalizedProjectId);

    if (!nextPreferVersionView && !projection.summary.hasPlayableMediaLayers) {
      kernel.current?.ensureWorkspaceImageTimelineInitialized(normalizedProjectId, normalizedWorkspaceId);
    }
  }, [kernel]);

  const updateTimelineTrackTiming = useCallback<UseWorkspaceTimelineReturnType['updateTimelineTrackTiming']>((layerId, patch, options) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return;

    applyLayerPatch(kernel, normalizedLayerId, (layer) => {
      const currentStartMs = Math.max(0, Math.round(toFiniteTimelineMs(layer.timelineStartMs) ?? 0));
      const rawStartMs = typeof patch.startMs === 'number' ? patch.startMs : currentStartMs;
      const startMs = Math.max(0, Math.round(toFiniteTimelineMs(rawStartMs) ?? currentStartMs));

      const currentEndMs = toFiniteTimelineMs(layer.timelineEndMs);
      const rawEndMs = Object.prototype.hasOwnProperty.call(patch, 'endMs') ? patch.endMs : currentEndMs;
      const endMs = rawEndMs === null || typeof rawEndMs === 'undefined'
        ? null
        : Math.max(startMs + MIN_TRACK_SPAN_MS, Math.round(toFiniteTimelineMs(rawEndMs) ?? (startMs + MIN_TRACK_SPAN_MS)));

      const currentSourceInMs = Math.max(0, Math.round(toFiniteTimelineMs(layer.timelineSourceInMs) ?? 0));
      const rawSourceInMs = typeof patch.sourceInMs === 'number' ? patch.sourceInMs : currentSourceInMs;
      const sourceInMs = Math.max(0, Math.round(toFiniteTimelineMs(rawSourceInMs) ?? currentSourceInMs));

      return {
        ...layer,
        timelineStartMs: startMs,
        timelineEndMs: endMs,
        timelineSourceInMs: sourceInMs,
      };
    }, options);
  }, [kernel]);

  const updateTimelineTrackAudio = useCallback<UseWorkspaceTimelineReturnType['updateTimelineTrackAudio']>((layerId, patch, options) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return;

    const paramPatch: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(patch, 'mutedInTimeline')) {
      paramPatch[TIMELINE_MUTE_AUDIO_PARAM_KEY] = patch.mutedInTimeline;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'audioOnly')) {
      paramPatch[TIMELINE_AUDIO_ONLY_PARAM_KEY] = patch.audioOnly;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'audioGain')) {
      paramPatch[TIMELINE_AUDIO_GAIN_PARAM_KEY] = typeof patch.audioGain === 'number'
        ? Math.round(clampTimelineNumber(patch.audioGain, 0, AUDIO_GAIN_MAX) * 1000) / 1000
        : patch.audioGain;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'separatedFromLayerId')) {
      paramPatch[TIMELINE_SEPARATED_FROM_LAYER_ID_PARAM_KEY] = patch.separatedFromLayerId;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'fadeInRatio')) {
      paramPatch[TIMELINE_FADE_IN_RATIO_PARAM_KEY] = typeof patch.fadeInRatio === 'number'
        ? clampTimelineNumber(patch.fadeInRatio, 0, 1)
        : patch.fadeInRatio;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'fadeOutRatio')) {
      paramPatch[TIMELINE_FADE_OUT_RATIO_PARAM_KEY] = typeof patch.fadeOutRatio === 'number'
        ? clampTimelineNumber(patch.fadeOutRatio, 0, 1)
        : patch.fadeOutRatio;
    }

    applyLayerPatch(kernel, normalizedLayerId, (layer) => ({
      ...layer,
      params: mergeTimelineParams(layer.params, paramPatch) as Layer['params'],
    }), options);
  }, [kernel]);

  const toggleTimelineTrackFade = useCallback<UseWorkspaceTimelineReturnType['toggleTimelineTrackFade']>((layerId, effect) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return;
    const track = kernel.current.getWorkspaceTimelineTrack(normalizedLayerId);
    // A fade is a visual ramp: audio tracks are shaped by gain, not by fades.
    if (!track || track.layerType === 'audio') return;

    const isFadeIn = effect === 'fade-in';
    const isEnabled = (isFadeIn ? track.fadeInRatio : track.fadeOutRatio) > TIMELINE_EFFECT_ENABLED_EPSILON;
    const nextRatio = isEnabled ? 0 : DEFAULT_TIMELINE_EFFECT_RATIO;

    kernel.current.updateTimelineTrackAudio(
      normalizedLayerId,
      isFadeIn ? { fadeInRatio: nextRatio } : { fadeOutRatio: nextRatio },
      {
        undoable: true,
        description: `${isEnabled ? 'Remove' : 'Add'} ${isFadeIn ? 'Fade In' : 'Fade Out'} on "${track.name}"`,
      }
    );
  }, [kernel]);

  const updateTimelineTrackLane = useCallback<UseWorkspaceTimelineReturnType['updateTimelineTrackLane']>((layerId, patch, options) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return;

    const paramPatch: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(patch, 'laneId')) {
      paramPatch[TIMELINE_LANE_ID_PARAM_KEY] = patch.laneId;
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'laneOrder')) {
      paramPatch[TIMELINE_LANE_ORDER_PARAM_KEY] = typeof patch.laneOrder === 'number' && Number.isFinite(patch.laneOrder)
        ? Math.round(patch.laneOrder)
        : patch.laneOrder;
    }

    applyLayerPatch(kernel, normalizedLayerId, (layer) => ({
      ...layer,
      params: mergeTimelineParams(layer.params, paramPatch) as Layer['params'],
    }), options);
  }, [kernel]);

  const reorderWorkspaceTimelineTracks = useCallback<UseWorkspaceTimelineReturnType['reorderWorkspaceTimelineTracks']>((
    projectId,
    workspaceId,
    orderedTrackLayerIds,
    options
  ) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId) return;

    const workspace = kernel.current.getWorkspace(normalizedWorkspaceId);
    const projection = resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId);
    if (!workspace || !projection) return;

    const existingLayerIds = Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
      : [];
    const currentTrackIdSet = new Set(projection.trackLayerIds);
    const ordered = uniqueStringIds(orderedTrackLayerIds).filter((layerId) => currentTrackIdSet.has(layerId));
    const finalTrackIds = [
      ...ordered,
      ...projection.trackLayerIds.filter((layerId) => !ordered.includes(layerId)),
    ];
    if (finalTrackIds.length === 0) return;

    let trackCursor = 0;
    const nextLayerIds = existingLayerIds.map((layerId) => {
      if (!currentTrackIdSet.has(layerId)) return layerId;
      const replacement = finalTrackIds[trackCursor] ?? layerId;
      trackCursor += 1;
      return replacement;
    });

    const previousWorkspace = { ...workspace, layerIds: [...existingLayerIds] };
    const previousLayersById = existingLayerIds.reduce<Record<string, Layer>>((acc, layerId) => {
      const layer = kernel.current?.getLayerRegistry()[layerId];
      if (layer) acc[layerId] = cloneLayerForTimelineUndo(layer);
      return acc;
    }, {});

    const applyNext = () => {
      kernel.current?.setWorkspace(normalizedWorkspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        layerIds: nextLayerIds,
      }), normalizedProjectId);
      nextLayerIds.forEach((layerId, index) => {
        kernel.current?.setLayer(layerId, (layer) => ({
          ...layer,
          renderOrder: index,
        }));
      });
    };

    const restorePrevious = () => {
      Object.entries(previousLayersById).forEach(([layerId, layer]) => {
        kernel.current?.setLayer(layerId, () => layer);
      });
      kernel.current?.setWorkspace(normalizedWorkspaceId, () => previousWorkspace, normalizedProjectId);
    };

    if (options?.undoable) {
      kernel.current?.executeAction({
        description: options.description ?? 'Reorder timeline tracks',
        redo: applyNext,
        undo: restorePrevious,
      });
      return;
    }
    applyNext();
  }, [kernel]);

  const duplicateWorkspaceTimelineTrack = useCallback<UseWorkspaceTimelineReturnType['duplicateWorkspaceTimelineTrack']>((projectId, workspaceId, layerId) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId || !normalizedLayerId) return;

    const workspace = kernel.current.getWorkspace(normalizedWorkspaceId);
    const track = resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId)?.tracks.find((item) => item.layerId === normalizedLayerId) ?? null;
    if (!workspace || !track) return;

    const workspaceLayerIdsBefore = Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((id): id is string => typeof id === 'string')
      : [];
    const originalLayerIndex = workspaceLayerIdsBefore.indexOf(normalizedLayerId);
    if (originalLayerIndex < 0) return;

    const beforeIds = new Set(workspaceLayerIdsBefore);
    kernel.current?.duplicateLayer(normalizedLayerId);
    const layerIdsAfterDuplicate = kernel.current?.getWorkspace(normalizedWorkspaceId)?.layerIds ?? [];
    const duplicatedLayerId = layerIdsAfterDuplicate.find((id) => !beforeIds.has(id)) ?? null;
    if (!duplicatedLayerId) return;

    const duplicatedLayerSnapshot = kernel.current?.getLayerRegistry()[duplicatedLayerId];
    if (!duplicatedLayerSnapshot) return;
    const previousWorkspace = { ...workspace, layerIds: [...workspaceLayerIdsBefore] };

    kernel.current?.executeAction({
      description: `Duplicate track "${track.name}"`,
      redo: () => {
        kernel.current?.setLayer(duplicatedLayerId, (layer) => ({
          ...layer,
          ...cloneLayerForTimelineUndo(duplicatedLayerSnapshot),
          visible: true,
          timelineStartMs: Math.round(track.startMs),
          timelineEndMs: track.endMs === null ? null : Math.round(track.endMs),
          timelineSourceInMs: Math.round(track.sourceInMs),
          pulseToken: Date.now(),
        }));
        kernel.current?.setWorkspace(normalizedWorkspaceId, (currentWorkspace) => {
          const currentLayerIds = Array.isArray(currentWorkspace.layerIds)
            ? currentWorkspace.layerIds.filter((id): id is string => typeof id === 'string')
            : [];
          const withoutDuplicate = currentLayerIds.filter((id) => id !== duplicatedLayerId);
          const insertionIndex = clampTimelineNumber(originalLayerIndex + 1, 0, withoutDuplicate.length);
          const nextLayerIds = [
            ...withoutDuplicate.slice(0, insertionIndex),
            duplicatedLayerId,
            ...withoutDuplicate.slice(insertionIndex),
          ];
          nextLayerIds.forEach((id, index) => {
            kernel.current?.setLayer(id, (layer) => ({ ...layer, renderOrder: index }));
          });
          return { ...currentWorkspace, layerIds: nextLayerIds };
        }, normalizedProjectId);
      },
      undo: () => {
        kernel.current?.setLayer(duplicatedLayerId, (layer) => ({
          ...layer,
          visible: false,
          pulseToken: Date.now(),
        }));
        kernel.current?.setWorkspace(normalizedWorkspaceId, () => previousWorkspace, normalizedProjectId);
        workspaceLayerIdsBefore.forEach((id, index) => {
          kernel.current?.setLayer(id, (layer) => ({ ...layer, renderOrder: index }));
        });
      },
    });
  }, [kernel]);

  const splitWorkspaceTimelineTrack = useCallback<UseWorkspaceTimelineReturnType['splitWorkspaceTimelineTrack']>((projectId, workspaceId, layerId, splitMs) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId || !normalizedLayerId || !Number.isFinite(splitMs)) return;

    const workspace = kernel.current.getWorkspace(normalizedWorkspaceId);
    const projection = resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId);
    const track = projection?.tracks.find((item) => item.layerId === normalizedLayerId) ?? null;
    const originalLayer = kernel.current.getLayerRegistry()[normalizedLayerId];
    if (!workspace || !projection || !track || !originalLayer) return;

    const trackEndMs = track.endMs ?? Math.max(track.startMs + MIN_TRACK_SPAN_MS * 2, projection.summary.durationMs);
    const splitCursorMs = Math.round(clampTimelineNumber(splitMs, track.startMs, trackEndMs));
    const leftDurationMs = splitCursorMs - track.startMs;
    const rightDurationMs = trackEndMs - splitCursorMs;
    if (leftDurationMs < MIN_TRACK_SPAN_MS || rightDurationMs < MIN_TRACK_SPAN_MS) return;

    const workspaceLayerIdsBefore = Array.isArray(workspace.layerIds)
      ? workspace.layerIds.filter((id): id is string => typeof id === 'string')
      : [];
    const originalLayerIndex = workspaceLayerIdsBefore.indexOf(normalizedLayerId);
    if (originalLayerIndex < 0) return;

    const firstBeforeIds = new Set(workspaceLayerIdsBefore);
    kernel.current?.duplicateLayer(normalizedLayerId);
    const layerIdsAfterFirstDuplicate = kernel.current?.getWorkspace(normalizedWorkspaceId)?.layerIds ?? [];
    const leftLayerId = layerIdsAfterFirstDuplicate.find((id) => !firstBeforeIds.has(id)) ?? null;
    if (!leftLayerId) return;

    const secondBeforeIds = new Set(layerIdsAfterFirstDuplicate);
    kernel.current?.duplicateLayer(normalizedLayerId);
    const layerIdsAfterSecondDuplicate = kernel.current?.getWorkspace(normalizedWorkspaceId)?.layerIds ?? [];
    const rightLayerId = layerIdsAfterSecondDuplicate.find((id) => !secondBeforeIds.has(id)) ?? null;
    if (!rightLayerId) return;

    const originalSnapshot = cloneLayerForTimelineUndo(originalLayer);
    const leftSnapshot = kernel.current?.getLayerRegistry()[leftLayerId];
    const rightSnapshot = kernel.current?.getLayerRegistry()[rightLayerId];
    if (!leftSnapshot || !rightSnapshot) return;

    const baseName = originalLayer.name || 'Track';
    const sourceInForTrack = track.layerType === 'video' || track.layerType === 'audio' ? Math.round(track.sourceInMs) : 0;
    const rightSourceInMs = track.layerType === 'video' || track.layerType === 'audio'
      ? Math.round(track.sourceInMs + leftDurationMs)
      : 0;
    const timelineDurationMsBefore = Math.max(1, Math.round(toFiniteTimelineMs(workspace.timelineDurationMs) ?? projection.summary.durationMs));
    const timelineCursorMsBefore = clampCursorForDuration(toFiniteTimelineMs(workspace.timelineCursorMs) ?? 0, timelineDurationMsBefore);

    kernel.current?.executeAction({
      description: `Split track "${baseName}"`,
      redo: () => {
        kernel.current?.setLayer(normalizedLayerId, (layer) => ({
          ...layer,
          visible: false,
          pulseToken: Date.now(),
        }));
        kernel.current?.setLayer(leftLayerId, (layer) => ({
          ...layer,
          visible: true,
          name: `${baseName} (A)`,
          timelineStartMs: Math.round(track.startMs),
          timelineEndMs: splitCursorMs,
          timelineSourceInMs: sourceInForTrack,
          pulseToken: Date.now() + 1,
        }));
        kernel.current?.setLayer(rightLayerId, (layer) => ({
          ...layer,
          visible: true,
          name: `${baseName} (B)`,
          timelineStartMs: splitCursorMs,
          timelineEndMs: Math.round(trackEndMs),
          timelineSourceInMs: rightSourceInMs,
          pulseToken: Date.now() + 2,
        }));
        kernel.current?.setWorkspace(normalizedWorkspaceId, (currentWorkspace) => {
          const currentLayerIds = Array.isArray(currentWorkspace.layerIds)
            ? currentWorkspace.layerIds.filter((id): id is string => typeof id === 'string')
            : [];
          const withoutSplitGroup = currentLayerIds.filter((id) => id !== normalizedLayerId && id !== leftLayerId && id !== rightLayerId);
          const insertionIndex = clampTimelineNumber(originalLayerIndex, 0, withoutSplitGroup.length);
          const nextLayerIds = [
            ...withoutSplitGroup.slice(0, insertionIndex),
            leftLayerId,
            rightLayerId,
            ...withoutSplitGroup.slice(insertionIndex),
          ];
          nextLayerIds.forEach((id, index) => {
            kernel.current?.setLayer(id, (layer) => ({ ...layer, renderOrder: index }));
          });
          return {
            ...currentWorkspace,
            layerIds: nextLayerIds,
            timelineDurationMs: timelineDurationMsBefore,
            timelineCursorMs: clampCursorForDuration(toFiniteTimelineMs(currentWorkspace.timelineCursorMs) ?? 0, timelineDurationMsBefore),
          };
        }, normalizedProjectId);
      },
      undo: () => {
        kernel.current?.setLayer(normalizedLayerId, () => originalSnapshot);
        kernel.current?.setLayer(leftLayerId, (layer) => ({
          ...layer,
          ...cloneLayerForTimelineUndo(leftSnapshot),
          visible: false,
          pulseToken: Date.now() + 3,
        }));
        kernel.current?.setLayer(rightLayerId, (layer) => ({
          ...layer,
          ...cloneLayerForTimelineUndo(rightSnapshot),
          visible: false,
          pulseToken: Date.now() + 4,
        }));
        kernel.current?.setWorkspace(normalizedWorkspaceId, (currentWorkspace) => ({
          ...currentWorkspace,
          layerIds: [...workspaceLayerIdsBefore],
          timelineDurationMs: timelineDurationMsBefore,
          timelineCursorMs: timelineCursorMsBefore,
        }), normalizedProjectId);
        workspaceLayerIdsBefore.forEach((id, index) => {
          kernel.current?.setLayer(id, (layer) => ({ ...layer, renderOrder: index }));
        });
      },
    });
  }, [kernel]);

  const packWorkspaceTimelineTracks = useCallback<UseWorkspaceTimelineReturnType['packWorkspaceTimelineTracks']>((projectId, workspaceId, layerIds) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!kernel.current || !normalizedProjectId || !normalizedWorkspaceId) return;

    const projection = resolveProjection(kernel, normalizedProjectId, normalizedWorkspaceId);
    if (!projection) return;
    const targetIds = uniqueStringIds(layerIds).filter((id) => projection.trackLayerIds.includes(id));
    if (targetIds.length < 2) return;

    const targetTracks = targetIds
      .map((id) => projection.tracks.find((track) => track.layerId === id))
      .filter((track): track is WorkspaceTimelineTrackView => Boolean(track));
    if (targetTracks.length < 2) return;

    const laneId = targetTracks[0]?.laneId && targetTracks[0].laneId !== targetTracks[0].layerId
      ? targetTracks[0].laneId
      : `lane-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const laneOrder = targetTracks.reduce((min, track) => Math.min(min, track.laneOrder), Number.POSITIVE_INFINITY);
    const safeLaneOrder = Number.isFinite(laneOrder) ? Math.round(laneOrder) : targetTracks[0]?.renderOrder ?? 0;
    const previousLayersById = targetIds.reduce<Record<string, Layer>>((acc, id) => {
      const layer = kernel.current?.getLayerRegistry()[id];
      if (layer) acc[id] = cloneLayerForTimelineUndo(layer);
      return acc;
    }, {});

    kernel.current?.executeAction({
      description: `Pack ${targetTracks.length} tracks`,
      redo: () => {
        targetIds.forEach((id) => {
          kernel.current?.updateTimelineTrackLane(id, { laneId, laneOrder: safeLaneOrder });
        });
      },
      undo: () => {
        Object.entries(previousLayersById).forEach(([id, layer]) => {
          kernel.current?.setLayer(id, () => layer);
        });
      },
    });
  }, [kernel]);

  const uncompactWorkspaceTimelineTrack = useCallback<UseWorkspaceTimelineReturnType['uncompactWorkspaceTimelineTrack']>((layerId) => {
    const normalizedLayerId = normalizeStringId(layerId);
    if (!kernel.current || !normalizedLayerId) return;
    const track = kernel.current.getWorkspaceTimelineTrack(normalizedLayerId);
    const layer = kernel.current.getLayerRegistry()[normalizedLayerId];
    if (!track || !layer) return;
    const previousLayer = cloneLayerForTimelineUndo(layer);

    kernel.current.executeAction({
      description: `Uncompact track "${track.name}"`,
      redo: () => {
        kernel.current?.updateTimelineTrackLane(normalizedLayerId, {
          laneId: normalizedLayerId,
          laneOrder: track.renderOrder,
        });
      },
      undo: () => {
        kernel.current?.setLayer(normalizedLayerId, () => previousLayer);
      },
    });
  }, [kernel]);

  const removePendingTimelineGenerationTask = useCallback<UseWorkspaceTimelineReturnType['removePendingTimelineGenerationTask']>((projectId, taskId) => {
    const normalizedProjectId = normalizeStringId(projectId);
    const normalizedTaskId = normalizeStringId(taskId);
    if (!kernel.current || !normalizedProjectId || !normalizedTaskId) return;

    const task = kernel.current.getGenerationTasks(normalizedProjectId).find((item) => item.id === normalizedTaskId) ?? null;
    kernel.current.setGenerationTasks(normalizedProjectId, (previousTasks) => previousTasks.filter((item) => item.id !== normalizedTaskId));
    kernel.current.logUserAction('Pending Timeline Generation Task Removed', {
      projectId: normalizedProjectId,
      workspaceId: task?.workspaceId ?? null,
      taskId: normalizedTaskId,
    });
  }, [kernel]);

  const composeWorkspaceTimelineVideo = useCallback<UseWorkspaceTimelineReturnType['composeWorkspaceTimelineVideo']>(async ({
    workspaceId,
    startMs,
    endMs,
    outputWidth,
    outputHeight,
    fps,
    outputFormat,
    qualityPreset,
    includeAudio,
    signal,
    tracks,
    onStatus,
  }) => {
    const normalizedWorkspaceId = normalizeStringId(workspaceId);
    if (!normalizedWorkspaceId) {
      throw new Error('Workspace video composition is unavailable.');
    }
    if (!tracks || tracks.length === 0) {
      throw new Error('No visible timeline tracks available for export.');
    }
    const requestBody = {
      workspaceId: normalizedWorkspaceId,
      output: {
        width: normalizeVideoDimension(outputWidth),
        height: normalizeVideoDimension(outputHeight),
        fps: Math.max(8, Math.min(60, Math.round(fps))),
        format: outputFormat === 'mov' ? 'mov' : 'mp4',
        quality: qualityPreset === 'draft' ? 'draft' : (qualityPreset === 'high' ? 'high' : 'balanced'),
        includeAudio: includeAudio !== false,
        profile: 'export' as const,
      },
      range: {
        startMs: typeof startMs === 'number' && Number.isFinite(startMs) ? Math.max(0, Math.round(startMs)) : null,
        endMs: typeof endMs === 'number' && Number.isFinite(endMs) ? Math.max(0, Math.round(endMs)) : null,
      },
      tracks,
    };

    const prepareResponse = await fetch('/api/export/timeline-video?mode=prepare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!prepareResponse.ok) {
      const errorText = await prepareResponse.text().catch(() => '');
      throw new Error(errorText || `Timeline export prepare failed with status ${prepareResponse.status}.`);
    }

    const preparePayload = await prepareResponse.json() as {
      cacheKey?: string;
      status: TimelineCompositionStatus;
      realtime?: { enabled?: boolean };
    };
    const cacheKey = typeof preparePayload?.cacheKey === 'string' ? preparePayload.cacheKey.trim() : '';
    if (!cacheKey) {
      throw new Error('Timeline export did not return a cache key.');
    }
    onStatus?.(preparePayload.status);

    const statusPayload = await waitForTimelineExportReadyViaSocket({
      kernel,
      cacheKey,
      initialStatus: preparePayload.status,
      timeoutMs: 6 * 60 * 1000,
      abortSignal: signal,
      onStatus,
      socketEnabled: preparePayload.realtime?.enabled !== false,
    });

    if (statusPayload.status !== 'ready') {
      throw new Error('Timeline export did not reach ready state.');
    }

    const fileResponse = await fetch(`/api/export/timeline-video?mode=file&cacheKey=${encodeURIComponent(cacheKey)}`, {
      method: 'GET',
      signal,
    });
    if (!fileResponse.ok) {
      const errorText = await fileResponse.text().catch(() => '');
      throw new Error(errorText || `Timeline export file fetch failed with status ${fileResponse.status}.`);
    }
    const blob = await fileResponse.blob();
    if (!blob || blob.size <= 0) {
      throw new Error('Timeline export returned an empty video.');
    }
    return { blob, cacheKey };
  }, [kernel]);

  return {
    getWorkspaceTimelineSummary,
    getWorkspaceTimelineSnapshot,
    getWorkspaceTimelineProjection,
    getWorkspaceTimelineResolvedTracks,
    getWorkspaceTimelineTrack,
    getWorkspaceTimelineTrackById,
    getWorkspaceTimelineSelectedTracks,
    resolveWorkspaceTimelineTrackAtClientX,
    resolveWorkspaceTimelineSplitMsAtClientX,
    listWorkspaceTimelineTrackIds,
    setWorkspaceTimelineCursor,
    toggleWorkspaceTimelineView,
    ensureWorkspaceImageTimelineInitialized,
    updateTimelineTrackTiming,
    updateTimelineTrackAudio,
    toggleTimelineTrackFade,
    updateTimelineTrackLane,
    reorderWorkspaceTimelineTracks,
    splitWorkspaceTimelineTrack,
    duplicateWorkspaceTimelineTrack,
    packWorkspaceTimelineTracks,
    uncompactWorkspaceTimelineTrack,
    removePendingTimelineGenerationTask,
    composeWorkspaceTimelineVideo,
  };
};
